import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import SuicideIntentForm from "./SuicideIntentForm";
import { useDispatch } from "react-redux";
import { fetchSuicideIntentAssessment, updateSuicideIntentAssessment } from "../../../store/slices/suicideIntentSlice";

const EditSuicideIntent = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};
    const [formData, setFormData] = useState(null);

    // List of items and their options
    const itemsWithOptions = [
        { id: "isolation", options: ["Somebody present", "Somebody nearby, or in visual or vocal contact", "No one nearby or in visual or vocal contact"] },
        { id: "timing", options: ["Intervention is probable", "Intervention is not likely", "Intervention is highly unlikely"] },
        { id: "precautions", options: ["No precautions", "Passive precautions", "Active precautions"] },
        { id: "actingToGetHelp", options: ["Notified potential helper", "Contacted but did not specifically notify", "Did not contact or notify"] },
        { id: "finalActs", options: ["None", "Thought about or made some arrangements", "Made definite plans"] },
        { id: "activePreparation", options: ["None", "Minimal to moderate", "Extensive"] },
        { id: "suicideNote", options: ["Absence of note", "Note written, but torn up", "Presence of note"] },
        { id: "overtCommunication", options: ["None", "Equivocal communication", "Unequivocal communication"] },
        { id: "allegedPurpose", options: ["Manipulate environment", "Components of '0' and '2'", "Escape, surcease, solve problems"] },
        { id: "expectationsFatality", options: ["Death unlikely", "Death possible", "Death probable or certain"] },
        { id: "conceptionLethality", options: ["Did less than thought lethal", "Wasn't sure", "Equaled or exceeded expected lethality"] },
        { id: "seriousness", options: ["Did not seriously attempt", "Uncertain about seriousness", "Seriously attempted"] },
        { id: "attitudeLivingDying", options: ["Did not want to die", "Components of '0' and '2'", "Wanted to die"] },
        { id: "conceptionRescuability", options: ["Death unlikely if rescued", "Uncertain whether could be averted", "Certain of death even if rescued"] },
        { id: "premeditation", options: ["None; Impulsive", "Suicide contemplated ≤3h", "Suicide contemplated >3h"] },
        { id: "reactionAttempt", options: ["Feels foolish/ashamed", "Accepts attempt and failure", "Regrets failure"] },
        { id: "visualizationDeath", options: ["Life-after-death", "Never-ending sleep/darkness", "No thoughts about death"] },
        { id: "previousAttempts", options: ["None", "One or two", "Three or more"] },
        { id: "intakeAlcohol", options: ["Some intake, not related", "Enough to impair judgment", "Intentional intake to facilitate attempt"] },
        { id: "alcoholIntakeAlcohol", title: "Relationship between Alcohol Intake and Attempt", options: ["Some alcohol intake prior to but not related to attempt, reportedly not enough to impair judgment", "Enough alcohol intake to impair judgment, reality testing and diminish responsibility/impulse control", "Intentional intake of alcohol in order to facilitate implementation of suicide attempt"] },
        { id: "intakeDrug", options: ["Some intake, not related", "Enough to impair judgment", "Intentional intake to facilitate attempt"] },
    ];

    useEffect(() => {
        const loadAssessment = async () => {
            try {
                const result = await dispatch(fetchSuicideIntentAssessment(id)).unwrap();

                // Map all _score fields for radios and snake_case text fields
                const mappedData = {
                    clientName: result.client_name || "",
                    diagnosis: result.diagnosis || "",
                    assessedBy: result.assessed_by || "",
                    additionalComments: result.additional_comments || "",
                    clinicianName: result.clinician_name || "",
                    patientId: result.patient_id || null,
                    caseId: result.case_id || null,
                };

                // Map _score fields to radio button keys
                itemsWithOptions.forEach(item => {
                    const snake = item.id.replace(/[A-Z]/g, l => `_${l.toLowerCase()}`); // e.g., actingToGetHelp → acting_to_get_help
                    mappedData[item.id] = result[`${snake}_score`] !== undefined ? String(result[`${snake}_score`]) : "0";
                });

                setFormData(mappedData);
            } catch (error) {
                Swal.fire("Error", "Failed to fetch Suicide Intent Assessment.", "error");
            }
        };
        loadAssessment();
    }, [id, dispatch]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const toSnakeCase = (str) => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData) return;

        // Build question payload with _score and _value
        const questionPayload = {};
        itemsWithOptions.forEach(item => {
            const score = parseInt(formData[item.id] || 0, 10);
            const value = item.options[score] || "";
            questionPayload[`${toSnakeCase(item.id)}_score`] = score;
            questionPayload[`${toSnakeCase(item.id)}_value`] = value;
        });

        const total_score = Object.keys(questionPayload)
            .filter(k => k.endsWith("_score"))
            .reduce((sum, k) => sum + questionPayload[k], 0);

        const risk_level = total_score <= 10 ? "Low Risk" : total_score <= 19 ? "Medium Risk" : "High Risk";

        const payload = {
            patient_id: formData.patientId || null,
            case_id: formData.caseId || null,
            client_name: formData.clientName || "",
            diagnosis: formData.diagnosis || "",
            assessed_by: formData.assessedBy || "",
            ...questionPayload,
            total_score,
            risk_level,
            additional_comments: formData.additionalComments,
            clinician_name: formData.clinicianName,
            status: "active",
        };

        try {
            await dispatch(updateSuicideIntentAssessment({ case_id: caseId, assessmentData: payload })).unwrap();
            Swal.fire("Updated!", `Suicide Intent Scale updated. Total Score: ${total_score} → ${risk_level}`, "success");
            // Stay on edit page
        } catch (error) {
            Swal.fire("Error", "Failed to update Suicide Intent Scale.", "error");
        }
    };

    if (!formData) return <div className="text-center p-5">Loading assessment...</div>;

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase04/suicide-intent/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <SuicideIntentForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary">
                                        <i className="ri-edit-2-line me-1"></i> Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditSuicideIntent;
